import React, { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useCurrentContext } from '../hooks/useCurrentContext';
import { useAgentLiveRefresh } from '../hooks/useAgentLiveRefresh';

/**
 * AgentContext — global agent UI state per ADR-017 §3.1.
 *
 * Combines route-derived context (from useCurrentContext) with manually-controlled
 * UI state (rail / canvas open) and meta (threadRef, unreadActivityCount,
 * agentStatus).
 *
 * For PR 1, only the route-derived fields are exercised. The UI-state fields
 * are wired as placeholders so later PRs can populate them without changing
 * the context shape:
 *   - isRailOpen / setIsRailOpen           — PR 4 (AdvocateRail)
 *   - isCanvasOpen / canvasType / setters  — PR 5 (WorkCanvas)
 *   - unreadActivityCount / setter         — PR 4 (ActivityFeed)
 *   - threadRef / setter                   — PR 3 (AskBar conversation persistence)
 *   - agentStatus                          — PR 2 (useAgentLiveRefresh)
 */

const AgentContext = createContext(null);

const DEFAULT_VALUE = {
  // Route-derived
  currentPage: 'unknown',
  currentObjectId: null,
  currentObjectType: null,

  // Page-pushed: human-readable name for the current object (e.g., real
  // property address). Pages call setCurrentObjectLabel(addr) once their
  // fetch resolves; AskBar / Trigger / suggestion endpoint use it.
  currentObjectLabel: null,
  setCurrentObjectLabel: () => {},

  // V4 routing — Ask responses captured here.
  // liveAnswers — append-only chronological log for the Advocate Rail.
  //   Each: { turn_id, query, body, target, canvas_type, confidence, confidence_pct, tool, when }
  liveAnswers: [],
  appendLiveAnswer: () => {},
  // canvasContent — current canvas response (null when no canvas open).
  canvasContent: null,
  setCanvasContent: () => {},

  // UI state
  isAskBarOpen: false,
  setIsAskBarOpen: () => {},
  askBarMode: 'launcher',
  setAskBarMode: () => {},
  openAskThread: () => {},
  askBarDraft: null,
  setAskBarDraft: () => {},
  openAskWithDraft: () => {},
  isRailOpen: false,
  setIsRailOpen: () => {},
  isCanvasOpen: false,
  setIsCanvasOpen: () => {},
  canvasType: null,
  setCanvasType: () => {},

  // Meta
  agentStatus: 'idle',
  setAgentStatus: () => {},
  unreadActivityCount: 0,
  setUnreadActivityCount: () => {},
  threadRef: null,
  setThreadRef: () => {},
};

/** Read the agent context from any component below the provider. */
export function useAgent() {
  const ctx = useContext(AgentContext);
  return ctx ?? DEFAULT_VALUE;
}

/** Wrap routed content so all components below can read agent context. */
export function AgentProvider({ children }) {
  const route = useCurrentContext();

  const [currentObjectLabel, setCurrentObjectLabel] = useState(null);
  const [liveAnswers, setLiveAnswers] = useState([]);
  const [canvasContent, setCanvasContent] = useState(null);
  const [isAskBarOpen, setIsAskBarOpen] = useState(false);
  // 'launcher' (tabs + suggestions) | 'thread' (this session's Q&A turns).
  // The bar flips to 'thread' the moment a conversational ask is submitted
  // and the rail's ask-receipts reopen it in thread view via openAskThread.
  const [askBarMode, setAskBarMode] = useState('launcher');
  const [askBarDraft, setAskBarDraft] = useState(null);
  const [isRailOpen, setIsRailOpen] = useState(false);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [canvasType, setCanvasType] = useState(null);
  const [agentStatus, setAgentStatus] = useState('idle');
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  const [threadRef, setThreadRef] = useState(null);
  // Delegation-loop ticket (UX charter 2026-07-07): set by AskBar when an
  // Act-intent item is submitted, rendered by the rail as the IN PROGRESS
  // band. Honesty lock: phases reflect the REAL request lifecycle
  // ('delegated' = /agent/ask in flight, 'ready' = routing returned and the
  // canvas is fetching/showing the artifact) — never fabricated tool steps.
  //   { label, tool, objectLabel, startedAt, phase: 'delegated'|'ready' }
  const [activeJob, setActiveJob] = useState(null);

  // When the route's object changes (id or type), reset the label so we
  // don't carry over a stale address from the previous page. The new page
  // will push its own label once its fetch resolves.
  React.useEffect(() => {
    setCurrentObjectLabel(null);
  }, [route.currentObjectId, route.currentObjectType]);

  // Upsert a live answer entry. Sources:
  //   - AskBar submission (optimistic, full body)
  //   - SSE live_answer initial scaffold (streaming=true, body="")
  //   - SSE live_answer_chunk (incremental body via body_so_far)
  //   - SSE live_answer final (streaming=false, body=full)
  // Merge by turn_id so the rail's typing cursor + body grow together
  // without race conditions between the optimistic POST response and
  // the chunk stream that arrives in parallel.
  const appendLiveAnswer = React.useCallback((answer) => {
    if (!answer?.turn_id) return;
    setLiveAnswers(prev => {
      const idx = prev.findIndex(a => a.turn_id === answer.turn_id);
      if (idx === -1) {
        return [{ ...answer, when: answer.when || Date.now() }, ...prev].slice(0, 50);
      }
      // Merge: prefer non-empty body (so streaming chunks beat empty
      // initial; a later optimistic full-body wins). Preserve original
      // when timestamp.
      const existing = prev[idx];
      const merged = {
        ...existing,
        ...answer,
        body: (answer.body && answer.body.length > (existing.body || '').length)
          ? answer.body : existing.body,
        when: existing.when,
      };
      const next = prev.slice();
      next[idx] = merged;
      return next;
    });
  }, []);

  // Apply a streaming chunk — find the entry by turn_id and grow its body.
  // Backend publishes `body_so_far` rather than just the new chunk so we
  // can treat each event idempotently (last-write-wins on body length).
  const appendLiveAnswerChunk = React.useCallback((turn_id, body_so_far) => {
    if (!turn_id || !body_so_far) return;
    setLiveAnswers(prev => {
      const idx = prev.findIndex(a => a.turn_id === turn_id);
      if (idx === -1) return prev;  // chunk before initial — race; ignore
      const existing = prev[idx];
      if ((existing.body || '').length >= body_so_far.length) return prev;
      const next = prev.slice();
      next[idx] = { ...existing, body: body_so_far, streaming: true };
      return next;
    });
  }, []);

  // Orchestrator job_progress → activeJob ticket (magnum-opus review
  // Finding 1). Real tool steps from real runs (forwarded emails, deal
  // events) render in the rail's IN PROGRESS band anywhere in the app.
  // Honesty lock: only server-emitted steps; failed runs clear, no theatre.
  const jobClearTimer = useRef(null);
  const applyJobProgress = React.useCallback((d) => {
    const ev = d.event_type;
    if (ev === 'orchestrator_started') {
      if (jobClearTimer.current) clearTimeout(jobClearTimer.current);
      setActiveJob({
        kind: 'job', dealId: d.deal_id,
        label: 'Working the deal file',
        objectLabel: null,
        startedAt: Date.now(), phase: 'delegated', steps: [],
      });
    } else if (ev === 'tool_executing') {
      setActiveJob(j => (j && j.kind === 'job' && j.dealId === d.deal_id)
        ? { ...j, steps: [...j.steps, { tool: d.tool, status: 'running' }] } : j);
    } else if (['tool_completed', 'tool_failed', 'tool_skipped', 'tool_denied'].includes(ev)) {
      setActiveJob(j => {
        if (!j || j.kind !== 'job' || j.dealId !== d.deal_id) return j;
        const steps = j.steps.map(s => (s.tool === d.tool && s.status === 'running')
          ? { ...s, status: ev.replace('tool_', ''), durationMs: d.duration_ms } : s);
        return { ...j, steps };
      });
    } else if (ev === 'orchestrator_completed') {
      setActiveJob(j => (j && j.kind === 'job' && j.dealId === d.deal_id)
        ? { ...j, phase: 'ready', elapsedMs: d.duration_ms } : j);
      if (jobClearTimer.current) clearTimeout(jobClearTimer.current);
      jobClearTimer.current = setTimeout(() => setActiveJob(null), 6000);
    } else if (ev === 'orchestrator_failed') {
      setActiveJob(j => (j && j.kind === 'job' && j.dealId === d.deal_id) ? null : j);
    }
  }, []);

  // Bridge user-scoped SSE → AgentContext.liveAnswers. Whenever the
  // backend publishes a `live_answer` event (POST /agent/ask publishes
  // via agent_broadcaster, or future orchestrator-driven asks), the
  // open rail picks it up automatically without polling.
  const { events: sseEvents, isConnected: sseConnected, lastEventAt: sseLastAt } =
    useAgentLiveRefresh();
  const lastConsumedAtRef = useRef(0);
  useEffect(() => {
    if (!sseEvents?.length) return;
    // Process any events newer than our last consumption mark.
    const newOnes = sseEvents.filter(e => e.ts > lastConsumedAtRef.current);
    if (!newOnes.length) return;
    lastConsumedAtRef.current = sseLastAt || Date.now();
    for (const e of newOnes) {
      if (e.eventType === 'live_answer' && e.data) {
        appendLiveAnswer({
          turn_id:        e.data.turn_id,
          query:          e.data.query,
          body:           e.data.body,
          target:         e.data.target,
          canvas_type:    e.data.canvas_type,
          confidence:     e.data.confidence,
          confidence_pct: e.data.confidence_pct,
          tool:           e.data.tool,
          grounding:      e.data.grounding,
          shape:          e.data.shape,
          options:        e.data.options,
          streaming:      e.data.streaming,
        });
      } else if (e.eventType === 'live_answer_chunk' && e.data) {
        appendLiveAnswerChunk(e.data.turn_id, e.data.body_so_far);
      } else if (e.eventType === 'job_progress' && e.data) {
        applyJobProgress(e.data);
      }
    }
  }, [sseEvents, sseLastAt, appendLiveAnswer, appendLiveAnswerChunk, applyJobProgress]);

  // Reopen the AskBar directly in thread view — the rail's ask receipts
  // deep-link here ("open thread ↗") per the spine charter: the receipt
  // points back into the conversation, it never re-renders the answer.
  const openAskThread = React.useCallback(() => {
    setAskBarMode('thread');
    setIsAskBarOpen(true);
  }, []);

  // Contextual explainers hand off to Ask without auto-submitting a question.
  // The member sees the exact draft, can edit it, then chooses whether to ask.
  const openAskWithDraft = React.useCallback((draft) => {
    const nextDraft = String(draft || '').trim();
    setAskBarMode('launcher');
    setAskBarDraft(nextDraft || null);
    setIsAskBarOpen(true);
  }, []);

  const value = useMemo(() => ({
    ...route,
    currentObjectLabel, setCurrentObjectLabel,
    liveAnswers, appendLiveAnswer,
    canvasContent, setCanvasContent,
    isAskBarOpen, setIsAskBarOpen,
    askBarMode, setAskBarMode, openAskThread,
    askBarDraft, setAskBarDraft, openAskWithDraft,
    isRailOpen, setIsRailOpen,
    isCanvasOpen, setIsCanvasOpen,
    canvasType, setCanvasType,
    agentStatus, setAgentStatus,
    unreadActivityCount, setUnreadActivityCount,
    threadRef, setThreadRef,
    activeJob, setActiveJob,
    // Expose SSE connection state for diagnostics / future "offline" indicators.
    sseConnected,
  }), [route, currentObjectLabel, liveAnswers, appendLiveAnswer, canvasContent,
       isAskBarOpen, askBarMode, openAskThread, askBarDraft, openAskWithDraft, isRailOpen, isCanvasOpen,
       canvasType, agentStatus, unreadActivityCount, threadRef, activeJob,
       sseConnected]);

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}
